// M29 — Register page. Two M29 changes layered on the original form:
//
// 1) Invite-code gate. The closed alpha requires an invite code. The
//    form validates + redeems the code via the
//    `redeem_alpha_invite_code` RPC BEFORE calling `signUp`. The RPC is
//    idempotent on (code, email) so a retry after a transient signup
//    failure doesn't burn a fresh use. If the code is invalid we never
//    call signUp at all.
//
// 2) Email-confirm awareness. With Supabase's "Confirm email" toggle
//    on, signUp returns no session — the user has to click the link in
//    their inbox first. We surface that with a success message instead
//    of trying to navigate them into the app (which would just bounce
//    them back to /login because they have no session yet).

import { useState, type FormEvent } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import QSLogo from '../../components/QSLogo';
import AuthPageFooter from '../../components/AuthPageFooter';
import { useAuth } from '../../contexts/useAuth';
import { friendlyAuthError } from '../../lib/authErrors';
import { redeemInviteCode, InviteCodeError } from '../../lib/inviteCodes';

export default function RegisterPage() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  // M30 — accept `?invite=...` so the maintainer can hand out a single
  // link that pre-fills the code (e.g.
  // https://quarterstack.app/register?invite=QS-ALPHA-1). The field is
  // still editable; we just seed initial state. Falls back to empty
  // when the param is absent.
  const [searchParams] = useSearchParams();
  const [inviteCode, setInviteCode] = useState(searchParams.get('invite') ?? '');
  const inviteFromUrl = !!searchParams.get('invite');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      // Gate before signup — if the code is invalid we never create an
      // auth user. Idempotent for the (code, email) pair, so retrying
      // after a downstream signup error is free.
      await redeemInviteCode(inviteCode, email);
    } catch (err) {
      setLoading(false);
      setError(err instanceof InviteCodeError ? err.message : 'Could not validate that invite code.');
      return;
    }

    const { error: signUpErr, needsConfirmation: nc } = await signUp(email, password, displayName);
    setLoading(false);

    if (signUpErr) {
      setError(friendlyAuthError(signUpErr.message));
      return;
    }
    if (nc) {
      // Email confirmation is enabled. Stop here and tell the user to
      // check their inbox; the app will pick them up after they click
      // the link.
      setNeedsConfirmation(true);
      return;
    }
    navigate('/', { replace: true });
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      bgcolor: 'background.default',
      px: 2,
      py: 4,
      gap: 3,
    }}>
      <Card sx={{ maxWidth: 420, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <QSLogo variant="icon" height={52} />
            <Box sx={{ mt: 1.5, mb: 0.5 }}>
              <QSLogo variant="wordmark" />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {needsConfirmation ? 'Check your email' : 'Create your account'}
            </Typography>
          </Box>

          {needsConfirmation ? (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Account created. We sent a confirmation link to{' '}
                <strong>{email}</strong>. Click it to finish signing up.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Once confirmed, you'll be signed in automatically. Don't see the
                email? Check your spam folder.
              </Typography>
              <Button
                component={RouterLink}
                to="/login"
                variant="outlined"
                fullWidth
              >
                Back to sign in
              </Button>
            </>
          ) : (
            <>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <Alert severity="info" sx={{ mb: 2 }}>
                QuarterStack is in closed alpha. You'll need an invite code from the
                maintainer to sign up.
              </Alert>

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  label="Invite code"
                  fullWidth
                  required
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  autoComplete="off"
                  sx={{ mb: 2 }}
                  helperText={inviteFromUrl ? 'Pre-filled from your invite link.' : 'Paste the code we sent you.'}
                />
                <TextField
                  label="Display Name"
                  fullWidth
                  required
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Password"
                  type="password"
                  fullWidth
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Confirm Password"
                  type="password"
                  fullWidth
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  sx={{ mb: 3 }}
                />
                <Button type="submit" variant="contained" fullWidth size="large" disabled={loading}>
                  {loading ? <CircularProgress size={24} /> : 'Create Account'}
                </Button>
                {/* M33 — Explicit acceptance of the legal docs at the
                    point of account creation. The links open the
                    standalone /privacy and /terms pages so the user can
                    read before signing up. No checkbox: creating an
                    account is itself the acceptance, which the text
                    above the button makes plain. */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1.5, textAlign: 'center' }}
                >
                  By creating an account you agree to our{' '}
                  <Link component={RouterLink} to="/privacy">Privacy</Link>
                  {' '}and{' '}
                  <Link component={RouterLink} to="/terms">Terms</Link>.
                  QuarterStack is a personal record-keeping tool — not
                  a registry, legal advice, or compliance service.
                </Typography>
              </Box>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Already have an account?{' '}
                  <Link component={RouterLink} to="/login">Sign in</Link>
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
      <AuthPageFooter />
    </Box>
  );
}
